import React, { useState } from 'react';
import { Box, Typography, Stack, Input, Button } from "@mui/material";
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { FormProvider } from 'website/components/Form/FormProvider';
import useYupValidationResolver from 'hook/useYupValidationResolver';
import { subscribeSchema } from '../validation';
import { subscribe } from 'website/api/subscribe';
import { ReactComponent as BellIcon } from '../assets/Bell.svg';
import { TurnstileWidget } from 'components/TurnstileWidget'; // Added

const getDefaultValues = () => ({
  email: '',
});

export const Subscribe = () => {
  const { t } = useTranslation();
  const [turnstileToken, setTurnstileToken] = useState(''); // Added
  const [success, setSuccess] = useState(false);
  const [emailValue, setEmailValue] = useState(getDefaultValues()?.email || '');
  const methods = useForm({
    mode: "onSubmit",
    resolver: useYupValidationResolver(subscribeSchema),
    defaultValues: getDefaultValues(),
  });

  const {
    formState: { errors },
  } = methods; 

  const handleChange = (input) => (e) => {
    methods.setValue(input, e.target.value);
    setEmailValue(e.target.value);
    setSuccess(false);
    methods.clearErrors();
  }

  const onSubmit = async (data) => {
    try {
      await subscribe({
        ...data,
        type: 'job_ad',
        'cf-turnstile-response': turnstileToken,
      });
      methods.reset({ ...getDefaultValues() });
      setSuccess(true);
      setEmailValue('');
    } catch (error) {
      setSuccess(false);
      console.log(error);
    }
  };

  return (
    <Stack
      sx={{
        flexDirection: {xs: "column", md: "row"},
        alignItems: "center",
        textAlign: {xs: "center", md: "left"},
        width: 1,
        pt: 3.75,
        pb: 6.25,
        // mt: 3.375,
        gap: {xs: 2.5, md: 1},
      }}
    >
      <BellIcon />
      <Stack gap={2} ml={{xs: 0, md: 1.5}}>
        <Typography
          variant="h5"
          fontWeight="600"
          color='text.black'
          lineHeight={"normal"}
          fontSize={"1.5625rem"}
        >
          {t('website:jobsList.subscribe.heading')}
        </Typography>
        <Typography
          variant="body4"
          color='#00000099'
        >
          {t('website:jobsList.subscribe.description')}
        </Typography>
      </Stack>
      <Box>
        {success ? (
          <Typography
            sx={{
              textAlign: {xs: "center", md: "left"},
              color: "text.black",
              fontFeatureSettings: '"liga" off, "cling" off',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {t('website:jobsList.subscribe.successMessage')}
          </Typography>
        ) : (
          <FormProvider methods={methods} onSubmit={onSubmit}>
            <Stack
              flex={1}
              gap={{xs: 2.5, md: 1}}
              sx={{
                flexDirection: {xs: "column", md: "row"},
                justifyContent: {xs: "center", md: "flex-end"},
              }}
            >
              <Box
                width={1}
                sx={({palette}) => ({
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  '& .MuiInputBase-root': {
                    backgroundColor: turnstileToken ? palette.background.paper : palette.background.subscribeInput,
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: '#13373F33',
                    borderBottom: '1px solid #13373F33 !important',
                  },
                  '& .MuiTypography-root': {
                    textAlign: "center",
                  },
                })}
              >
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
                  placeholder={t('website:jobsList.subscribe.emailPlaceholder')}
                  fullWidth
                  disabled={!turnstileToken}
                  onChange={handleChange("email")}
                  error={!!errors.email}
                  value={emailValue}
                  aria-describedby="email-error-text"
                  sx={({palette}) => ({
                    '& .MuiInputBase-input:-webkit-autofill': {
                      WebkitBoxShadow: `0 0 0 1000px ${palette.background.paper} inset`,
                      WebkitTextFillColor: palette.text.primary,
                      borderRadius: 4,
                    },
                    '& .MuiInputBase-input::placeholder': {
                      textAlign: {xs: "center", md: "left"},
                      color: "#767676",
                      fontFeatureSettings: '"liga" off, "cling" off',
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      lineHeight: '24px',
                    },
                    '&::before': {
                      borderBottom: 'none !important',
                    },
                    '&::after': {
                      borderBottom: 'none !important',
                    },
                  })}
                />
                {errors.email && (
                  <Typography id="email-error-text" style={{ color: 'red' }}>
                    {errors.email?.message}
                  </Typography>
                )}
                <Box>
                  <TurnstileWidget onSuccess={setTurnstileToken} />
                </Box>
              </Box>

              <Button
                size="large"
                variant="contained"
                type="submit"
                disabled={!turnstileToken} // Modified disabled prop
                sx={{
                  backgroundColor: "primary.main",
                  '&:hover': {
                    backgroundColor: "primary.main",
                  },
                  minWidth: 144,
                  whiteSpace: 'nowrap',
                  alignSelf: {xs: "center", md: "flex-start"},
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  lineHeight: "24px"
                }}
              >
                {t('website:jobsList.subscribe.subscribeButton')}
              </Button>
            </Stack>
          </FormProvider>
        )}
      </Box>
    </Stack>
  )
}